import { TriangleAlert } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { useConfig } from '../api/queries'
import { useAuth } from '../auth/context'
import { Button } from '../components/Button'
import { Spinner } from '../components/Spinner'
import { TelegramLoginWidget } from '../components/TelegramLoginWidget'
import { getInvite } from '../lib/invite'
import type { TelegramWidgetUser } from '../telegram'
import { AuthLayout } from './AuthLayout'

export function LoginScreen() {
  const config = useConfig()
  const { signInWithWidget, signInWithDev } = useAuth()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const invited = getInvite() !== null

  async function run(call: () => Promise<string | null>) {
    setPending(true)
    setError(null)
    const message = await call()
    setPending(false)
    setError(message)
  }

  if (config.isPending) {
    return (
      <AuthLayout>
        <div className="flex justify-center">
          <Spinner size={24} />
        </div>
      </AuthLayout>
    )
  }

  if (config.isError) {
    return (
      <AuthLayout>
        <div className="text-center">
          <h1 className="page-title">Сервер не отвечает</h1>
          <p className="mt-1 text-sm text-muted">Проверьте соединение и попробуйте ещё раз.</p>
          <Button className="mt-5" onClick={() => void config.refetch()}>
            Повторить
          </Button>
        </div>
      </AuthLayout>
    )
  }

  const { bot_username: bot, dev_login: devLogin } = config.data

  return (
    <AuthLayout>
      <div className="text-center">
        <h1 className="page-title">{invited ? 'Приглашение в команду' : 'Вход'}</h1>
        <p className="mt-1 text-sm text-pretty text-muted">
          {invited
            ? 'Войдите через Telegram, чтобы присоединиться.'
            : 'Войдите через Telegram. Пускаем только участников команды.'}
        </p>
      </div>

      <div className="mt-6">
        {bot ? (
          pending ? (
            <div className="flex h-10 items-center justify-center gap-2 text-sm text-muted">
              <Spinner size={16} /> Входим…
            </div>
          ) : (
            <TelegramLoginWidget
              botUsername={bot}
              onAuth={(user: TelegramWidgetUser) => void run(() => signInWithWidget(user))}
            />
          )
        ) : (
          <div className="flex gap-2.5 rounded-lg bg-surface p-3 text-sm text-muted ring-1 ring-line ring-inset">
            <TriangleAlert size={16} className="mt-0.5 shrink-0" />
            <span>Бот не настроен, поэтому вход через Telegram пока недоступен.</span>
          </div>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-4 text-center text-sm text-danger">
          {error}
        </p>
      )}

      {devLogin && <DevLoginForm disabled={pending} onSubmit={(id) => run(() => signInWithDev(id))} />}
    </AuthLayout>
  )
}

function DevLoginForm({ disabled, onSubmit }: { disabled: boolean; onSubmit: (id: number) => Promise<void> }) {
  const [value, setValue] = useState('')
  const id = Number(value)
  const valid = value.trim() !== '' && Number.isSafeInteger(id) && id > 0

  function submit(e: FormEvent) {
    e.preventDefault()
    if (valid) void onSubmit(id)
  }

  return (
    <form onSubmit={submit} className="mt-8 border-t border-line pt-5">
      <label htmlFor="dev-id" className="mb-1.5 block text-xs font-medium text-muted">
        Войти как разработчик
      </label>
      <div className="flex gap-2">
        <input
          id="dev-id"
          inputMode="numeric"
          autoComplete="off"
          placeholder="Telegram ID"
          value={value}
          onChange={(e) => setValue(e.target.value.replace(/\D/g, ''))}
          className="h-11 min-w-0 flex-1 rounded-lg bg-card px-3 text-[15px] ring-1 ring-line outline-none ring-inset placeholder:text-muted focus:ring-fg/40 desk:h-8 desk:text-[13px]"
        />
        <Button type="submit" disabled={!valid || disabled}>
          Войти
        </Button>
      </div>
      <p className="mt-1.5 text-xs text-muted">Только в режиме разработки.</p>
    </form>
  )
}
